'use client';

import type { CSSProperties } from 'react';

import { MessageCreatorSurface } from '@ps/features/bio/message-creator/MessageCreatorSurface';
import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';

interface Props {
  creatorId: string;
  creatorName: string;
  creatorAvatar?: string;
  dmMinimumSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  bioContext: BioContext;
  /** Creator bio home — where Back / close lands. */
  bioHref: string;
  /** Creator backdrop (image / gradient / color) painted behind the frame. */
  pageBackground: CSSProperties;
}

/**
 * Standalone host for `/<url_code>/chat` — the cold-load / direct-link / mobile
 * view. Full-screen on mobile; a bounded, centered phone-width frame (with
 * margins) on the creator's bio backdrop on desktop. NOT a dialog. Back leaves
 * via a hard `location.replace` so a subsequent browser Back skips this route.
 */
export function ChatStandalone({
  creatorId,
  creatorName,
  creatorAvatar,
  dmMinimumSubscriptionLevel,
  bioContext,
  bioHref,
  pageBackground,
}: Props) {
  const leaveToBio = () => {
    window.location.replace(bioHref);
  };

  return (
    <div className="relative flex min-h-dvh w-full items-center justify-center sm:p-4">
      <div className="fixed inset-0 -z-10" style={pageBackground} aria-hidden />

      <div className="flex h-dvh w-full flex-col overflow-hidden bg-[#121402] sm:h-[min(760px,92vh)] sm:w-[min(420px,calc(100vw-2rem))] sm:rounded-2xl sm:shadow-2xl">
        <MessageCreatorSurface
          creatorId={creatorId}
          creatorName={creatorName}
          creatorAvatar={creatorAvatar}
          dmMinimumSubscriptionLevel={dmMinimumSubscriptionLevel}
          bioContext={bioContext}
          onClose={leaveToBio}
        />
      </div>
    </div>
  );
}
